import React, { useState, useEffect } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import AddClubSuggestion from './AddClubSuggestion'
import BookSearchPreview from './BookSearchPreview'
import ConfirmDialog from './ConfirmDialog'
import { formatDate } from '../utils/dateFormat'

interface Book {
  id: number
  title: string
  author: string
  isbn?: string
  asin?: string
  bookshop_slug?: string
  description?: string
  cover_url?: string
  genre?: string
  first_publish_year?: number
  number_of_pages?: number
}

interface Suggestion {
  id: number
  notes?: string
  created_at: string
  vote_count: number
  user_voted: boolean
  book: Book
  suggested_by: {
    id: number
    name: string
    email: string
  }
}

interface ClubBookSuggestionsProps {
  clubId: number
  currentUser: {
    id: number
    name: string
  }
  preferredProvider?: string | null
  onProviderSelected?: (provider: string) => void
}

export default function ClubBookSuggestions({ clubId, currentUser, preferredProvider, onProviderSelected }: ClubBookSuggestionsProps) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddSuggestion, setShowAddSuggestion] = useState(false)
  const [votingId, setVotingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [previewBook, setPreviewBook] = useState<Book | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deletingSuggestionId, setDeletingSuggestionId] = useState<number | null>(null)
  const [userProvider, setUserProvider] = useState<string | null | undefined>(preferredProvider)

  useEffect(() => {
    fetchSuggestions()
  }, [clubId])

  // Update local provider when prop changes
  useEffect(() => {
    setUserProvider(preferredProvider)
  }, [preferredProvider])

  const fetchSuggestions = async () => {
    try {
      const response = await fetch(`/api/v1/clubs/${clubId}/club_book_suggestions`)
      if (response.ok) {
        const data = await response.json()
        setSuggestions(data.suggestions)
      }
    } catch (err) {
      console.error('Failed to fetch suggestions:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleVote = async (suggestionId: number) => {
    setVotingId(suggestionId)
    try {
      const response = await fetchWithCsrf(
        `/api/v1/clubs/${clubId}/club_book_suggestions/${suggestionId}/vote`,
        { method: 'POST' }
      )

      if (response.ok) {
        const data = await response.json()
        setSuggestions(suggestions.map(s =>
          s.id === suggestionId ? data.suggestion : s
        ))
      }
    } catch (err) {
      console.error('Failed to vote:', err)
    } finally {
      setVotingId(null)
    }
  }

  const handleDelete = (suggestionId: number) => {
    setDeletingSuggestionId(suggestionId)
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirmed = async () => {
    if (!deletingSuggestionId) return

    setShowDeleteConfirm(false)
    setDeletingId(deletingSuggestionId)
    try {
      const response = await fetchWithCsrf(
        `/api/v1/clubs/${clubId}/club_book_suggestions/${deletingSuggestionId}`,
        { method: 'DELETE' }
      )

      if (response.ok) {
        setSuggestions(suggestions.filter(s => s.id !== deletingSuggestionId))
      }
    } catch (err) {
      console.error('Failed to delete suggestion:', err)
    } finally {
      setDeletingId(null)
      setDeletingSuggestionId(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Book Suggestions</h2>
          <p className="text-sm text-gray-600 mt-1">Suggest books and vote for your favorites</p>
        </div>
        <button
          onClick={() => setShowAddSuggestion(true)}
          className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
        >
          + Suggest Book
        </button>
      </div>

      {suggestions.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 divide-y divide-gray-200">
          {suggestions.map(suggestion => (
            <div key={suggestion.id} className="p-4 hover:bg-gray-50 transition-colors">
              <div className="flex gap-4">
                {/* Vote Button */}
                <div className="flex flex-col items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleVote(suggestion.id)}
                    disabled={votingId === suggestion.id}
                    className={`p-2 rounded-lg transition-all ${
                      suggestion.user_voted
                        ? 'bg-indigo-100 text-indigo-600 hover:bg-indigo-200'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } disabled:opacity-50`}
                    title={suggestion.user_voted ? 'Remove vote' : 'Upvote'}
                  >
                    {votingId === suggestion.id ? (
                      <div className="w-5 h-5">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                  <span className={`text-sm font-semibold ${
                    suggestion.vote_count > 0 ? 'text-indigo-600' : 'text-gray-500'
                  }`}>
                    {suggestion.vote_count}
                  </span>
                </div>

                {/* Book Cover & Info - Clickable */}
                <button
                  onClick={() => setPreviewBook(suggestion.book)}
                  className="flex gap-4 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
                >
                  {suggestion.book.cover_url ? (
                    <img
                      src={suggestion.book.cover_url}
                      alt={suggestion.book.title}
                      className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                      </svg>
                    </div>
                  )}

                  {/* Book Info */}
                  <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 mb-1">{suggestion.book.title}</h3>
                  <p className="text-sm text-gray-600 mb-2">by {suggestion.book.author}</p>
                  {suggestion.notes && (
                    <p className="text-sm text-gray-700 mb-2 bg-gray-50 p-2 rounded italic">
                      "{suggestion.notes}"
                    </p>
                  )}
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Suggested by {suggestion.suggested_by.name}</span>
                    <span>•</span>
                    <span>{formatDate(suggestion.created_at)}</span>
                    {suggestion.book.first_publish_year && (
                      <>
                        <span>•</span>
                        <span>{suggestion.book.first_publish_year}</span>
                      </>
                    )}
                    {suggestion.book.number_of_pages && (
                      <>
                        <span>•</span>
                        <span>{suggestion.book.number_of_pages} pages</span>
                      </>
                    )}
                  </div>
                  <p className="text-xs text-indigo-600 mt-1">Click to see details</p>
                </div>
                </button>

                {/* Delete Button (only for own suggestions) */}
                {suggestion.suggested_by.id === currentUser.id && (
                  <button
                    onClick={() => handleDelete(suggestion.id)}
                    disabled={deletingId === suggestion.id}
                    className="text-red-600 hover:text-red-700 p-2 disabled:opacity-50 flex-shrink-0"
                    title="Delete suggestion"
                  >
                    {deletingId === suggestion.id ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full mb-4">
            <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No suggestions yet</h3>
          <p className="text-gray-600 mb-6">Be the first to suggest a book for the club!</p>
          <button
            onClick={() => setShowAddSuggestion(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white font-semibold rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-lg shadow-indigo-500/30"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Suggest a Book
          </button>
        </div>
      )}

      {/* Add Suggestion Modal */}
      {showAddSuggestion && (
        <AddClubSuggestion
          clubId={clubId}
          onClose={() => setShowAddSuggestion(false)}
          onSuccess={() => {
            setShowAddSuggestion(false)
            fetchSuggestions()
          }}
        />
      )}

      {/* Book Preview Modal */}
      {previewBook && (
        <BookSearchPreview
          book={previewBook}
          onClose={() => setPreviewBook(null)}
          onSelect={() => setPreviewBook(null)}
          userProvider={userProvider}
          onProviderSelected={(provider) => {
            setUserProvider(provider)
            onProviderSelected?.(provider)
          }}
          showSelectButton={false}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <ConfirmDialog
          message="Are you sure you want to delete this suggestion?"
          onConfirm={handleDeleteConfirmed}
          onCancel={() => {
            setShowDeleteConfirm(false)
            setDeletingSuggestionId(null)
          }}
          confirmText="Delete"
        />
      )}
    </div>
  )
}
